import React, { useEffect, useMemo } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import weakKey from 'weak-key';

import { Chip, Snackbar, TabsNav } from '../../../components/atoms';
import { LoadingSpinner } from '../../../components/layouts';
import { MenuActionsType, MenuSectionNavType } from '../../../types/menu.d';
import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';
import * as Data from '../../../weborder/Data';
import { Utilities } from '../../../weborder/Utilities';
import { menuUtils } from '../utils';
import { MenuSection } from './index';

/* eslint-disable import/no-commonjs */
// window['__forceSmoothScrollPolyfill__'] = true;
// require('smoothscroll-polyfill').polyfill();
/* eslint-enable import/no-commonjs */

interface Props {
  actions: MenuActionsType;
  basket: BasketControl.Data;
  concessionStoreId?: number;
  currency: Api.Currency;
  expandedState: number[];
  isOutsideDeliveryZone: boolean;
  languageCode: string;
  menuData: Api.MenuReposnse | Api.ReadonlyMenuResponse | null;
  restaurantData: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail;
  translate: any;
  vrId: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  menu: {},

  message: {},

  navigation: {
    '& .mobileNav': {
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      padding: theme.spacing(0, 2),

      '& [class*=MuiTab-root]': {
        color: theme.palette.grey[800],
        fontSize: theme.typography.pxToRem(14),
        margin: 0,
        marginRight: theme.spacing(2),
        minWidth: 0,
      },
    },

    '& .desktopNav': {
      display: 'none',
    },
  },

  sections: {},

  [theme.breakpoints.up('sm')]: {
    navigation: {
      '& .mobileNav': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        padding: theme.spacing(0, 3),
      },
    },

    message: {
      margin: theme.spacing(0, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    navigation: {
      '& .desktopNav': {
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'flex-start',
        paddingBottom: theme.spacing(2.5),
        paddingTop: theme.spacing(2),

        '& .MuiChip-root': {
          marginBottom: theme.spacing(1),
          marginRight: theme.spacing(1),
        },
      },

      '& .mobileNav': {
        display: 'none',
      },
    },

    message: {
      margin: theme.spacing(0),
    },
  },
}));

export function Menu(props: Props): JSX.Element | null {
  const {
    actions,
    basket,
    concessionStoreId,
    currency,
    expandedState,
    languageCode,
    menuData,
    restaurantData,
    translate,
    vrId,
  } = props;

  const classes = useStyles();

  useEffect(() => {
    if (menuData) {
      setupExpandedStates();
    }
  }, [expandedState, menuData]);

  const checkSectionAvailabilityMode = (menuSection) => {
    switch (menuSection.MenuSectionAvailability.AvailabilityMode) {
      case Api.MenuEntityAvailabilityMode.DisplayAlways:
        return true;

      case Api.MenuEntityAvailabilityMode.DisplayBasedOnTimes: {
        if (
          menuSection.MenuSectionAvailability.AvailableTimes !== null &&
          Utilities.isInPeriod(
            menuSection.MenuSectionAvailability.AvailableTimes,
            restaurantData.TimeZoneInfoId,
          )
        ) {
          return true;
        }
        break;
      }

      case Api.MenuEntityAvailabilityMode
        .DisplayAlwaysStartCollapsedBasedOnTimes: {
        if (
          menuSection.MenuSectionAvailability.AvailableTimes !== null &&
          Utilities.isInPeriod(
            menuSection.MenuSectionAvailability.AvailableTimes,
            restaurantData.TimeZoneInfoId,
          )
        ) {
          return true;
        }
        break;
      }

      default:
        return false;
    }

    return false;
  };

  const setupExpandedStates = () => {
    if (typeof expandedState === 'undefined') {
      let expandedSections = [];

      menuData.MenuSections.forEach((menuSection) => {
        if (
          !menuSection.MenuSectionAvailability ||
          checkSectionAvailabilityMode(menuSection)
        ) {
          expandedSections.push(menuSection.MenuSectionId);
        }
      });

      if (
        menuData.MenuSectionBehaviour === Api.MenuSectionBehaviour.ExpandSingle
      ) {
        expandedSections = expandedSections.slice(0, 1);
      }

      actions.initExpanded(vrId, expandedSections);
    }

    if (
      menuData &&
      menuData.MenuSectionBehaviour !== menuData.MenuSectionBehaviour &&
      menuData.MenuSectionBehaviour === Api.MenuSectionBehaviour.ExpandSingle
    ) {
      const newExpandedSections = expandedState.slice(-1);
      actions.initExpanded(vrId, newExpandedSections);
    }
  };

  // const handleSectionClick = (menuSection) => {
  //   const menuSectionElement = document.getElementById(
  //     menuSection.MenuSectionId,
  //   );

  //   // If MenuBehaviour allows only one expanded section at a time then wait
  //   // for previous section to collapse if expanded
  //   // const OFFSET_TIME =
  //   //   menu.MenuSectionBehaviour == Api.MenuSectionBehaviour.ExpandSingle &&
  //   //   expandedState.length !== 0
  //   //     ? COLLAPSE_TIME
  //   //     : 0;

  //   if (menu.MenuSectionBehaviour == Api.MenuSectionBehaviour.ExpandSingle) {
  //     actions.initExpanded(vrId, [menuSection.MenuSectionId]);
  //   } else {
  //     actions.addExpanded(vrId, menuSection.MenuSectionId);
  //   }

  //   if (menuSectionElement) {
  //     //If there is a wait collapse the previous section
  //     // if (OFFSET_TIME !== 0) {
  //     //   this.setState({}, () => {
  //     //     sectionLinksScroll(OFFSET_TIME, menuSection.MenuSectionId);
  //     //   });
  //     // } else {
  //     sectionLinksScroll(); // OFFSET_TIME, menuSection.MenuSectionId);
  //     // }
  //   }
  // };

  const checkMenuSectionTimeAvilability = (menuSection) => {
    if (menuSection.MenuSectionAvailability) {
      const menuSectionAvailability = menuSection.MenuSectionAvailability;
      const isMenuSectionInPeriod =
        menuSectionAvailability.AvailableTimes !== null &&
        Utilities.isInPeriod(
          menuSectionAvailability.AvailableTimes,
          restaurantData.TimeZoneInfoId,
        );

      if (
        !isMenuSectionInPeriod &&
        menuSectionAvailability.AvailabilityMode ===
          Api.MenuEntityAvailabilityMode.DisplayBasedOnTimes
      ) {
        return false;
      }
    }

    if (!menuSection.IsAvailable) {
      return false;
    }

    // Hacky fix for now
    if (
      menuSection.Name === 'Hidden Promotional Items' ||
      (concessionStoreId && concessionStoreId != menuSection.ConcessionStoreId)
    ) {
      return false;
    }

    return true;
  };

  const getAvailableMenuSections = () => {
    const menuSections = Data.Sorted(menuData.MenuSections);
    return menuSections
      .map((menuSection) =>
        checkMenuSectionTimeAvilability(menuSection) ? menuSection : null,
      )
      .filter((section) => section !== null);
  };

  const availableMenuSections = useMemo(() => {
    if (!menuData) {
      return [];
    }

    return getAvailableMenuSections();
  }, [menuData]);

  const getNavTitle = (name) => {
    if (name !== '' && name !== 'Untitled Section') {
      return name;
    }

    return translate('Untitled_section');
  };

  const renderMenuMessage = (): JSX.Element | null => {
    if (!restaurantData.MenuMessage) {
      return null;
    }

    const menuMessageWithLineBreaks = Utilities.stringToJSX(
      restaurantData.MenuMessage.split('\n'),
      (part: string) => [part, <br key={weakKey({ part })} />],
    );
    menuMessageWithLineBreaks.pop();

    return <Snackbar message={menuMessageWithLineBreaks} severity="info" />;
  };

  const handleNavClick = (sectionId) => {
    if (
      menuData.MenuSectionBehaviour == Api.MenuSectionBehaviour.ExpandSingle
    ) {
      actions.initExpanded(vrId, [sectionId]);
    } else {
      actions.onAddExpanded(vrId, sectionId);
    }

    const targetId = `menuSection-${sectionId.toString()}`;
    menuUtils.handleScrollToSection(targetId);
  };

  const getSectionNavConfig = (menuSections) =>
    menuSections.map(
      (menuSection) =>
        ({
          id: menuSection.MenuSectionId,
          label: getNavTitle(menuSection.Name),
        } as MenuSectionNavType),
    );

  const renderDesktopMenuNav = (availableSectionsNav): JSX.Element => (
    <div className="desktopNav">
      {availableSectionsNav.map((chip) => (
        <Chip
          data-id={chip.id}
          key={weakKey({ chip })}
          label={chip.label}
          onClick={() => handleNavClick(chip.id)}
        />
      ))}
    </div>
  );

  const renderMobileMenuNav = (availableSectionsNav): JSX.Element => (
    <TabsNav
      className="mobileNav"
      handleTabClick={handleNavClick}
      TabIndicatorProps={{ style: { display: 'none' } }}
      tabs={availableSectionsNav}
    />
  );

  const menuNavigation = useMemo(() => {
    if (!availableMenuSections || !availableMenuSections.length) {
      return null;
    }

    const availableSectionsNav = getSectionNavConfig(availableMenuSections);

    return (
      <nav>
        {renderDesktopMenuNav(availableSectionsNav)}
        {renderMobileMenuNav(availableSectionsNav)}
      </nav>
    );
  }, [availableMenuSections]);

  const getExpandedState = (sectionId) => {
    if (typeof expandedState === 'undefined') {
      return false;
    }

    return expandedState.some((section) => section === sectionId);
  };

  const renderMenuSections = () => {
    const menuSections: JSX.Element[] = availableMenuSections.map(
      (menuSection) => {
        const menuSectionData = {
          description: menuSection.Description,
          expandBehaviour: menuData.MenuSectionBehaviour,
          imageUrl: menuSection.ImageUrl,
          items: menuSection.MenuItems,
          name: menuSection.Name,
          sectionId: menuSection.MenuSectionId,
        };
        const isExpanded = getExpandedState(menuSection.MenuSectionId);

        return (
          <MenuSection
            actions={actions}
            basket={basket}
            currency={currency}
            languageCode={languageCode}
            expandedState={isExpanded}
            key={menuSection.MenuSectionId}
            menuId={menuData.MenuId}
            menuSectionData={menuSectionData}
            translate={translate}
            vrId={vrId}
          />
        );
      },
    );

    return menuSections;
  };

  if (!menuData) {
    return <LoadingSpinner variant="panel" />;
  }

  const displayMenuSectionNav = !!menuData.DisplaySectionLinks;

  return (
    <div className={classes.menu}>
      <div className={classes.message}>{renderMenuMessage()}</div>

      {displayMenuSectionNav && (
        <div className={classes.navigation}>{menuNavigation}</div>
      )}

      <div className={classes.sections} data-fd="menu-content" role="tablist">
        {renderMenuSections()}
      </div>
    </div>
  );
}
